import React, { useState } from "react";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, LuxuryButton, StatusBadge, EmptyState } from "@/components/lux";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import { Banknote, History, FileSpreadsheet, FileText } from "lucide-react";

const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]/50";
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

export default function AdminSendFund() {
  const [f, setF] = useState({ member_id: "", amount: "", type: "credit" });
  const [busy, setBusy] = useState(false);
  const { data: hist, loading, reload } = useFetch("/admin/fund-history");
  const [hf, setHf] = useState({ from: "", to: "", dir: "all" });
  const upd = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.member_id.trim() || !Number(f.amount)) return toast.error("Enter a valid User ID and amount");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/send-fund", { member_id: f.member_id.trim(), amount: Number(f.amount), type: f.type });
      toast.success(data.message);
      setF((s) => ({ ...s, member_id: "", amount: "" }));
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const allRecords = hist?.records || [];
  const records = allRecords.filter((r) => {
    if (hf.dir !== "all" && r.direction !== hf.dir) return false;
    if (r.created_at) {
      const d = new Date(r.created_at);
      if (hf.from && d < new Date(hf.from + "T00:00:00")) return false;
      if (hf.to && d > new Date(hf.to + "T23:59:59")) return false;
    }
    return true;
  });
  const HEAD = ["#", "User ID", "Name", "Amount", "Type", "By Admin", "Date"];
  const exportRows = () => records.map((r, i) => [i + 1, r.member_id || "", r.name || "", money(r.amount), r.type, r.admin || "", fmtDate(r.created_at)]);
  const download = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  const exportCSV = () => {
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    download("\uFEFF" + [HEAD, ...exportRows()].map((r) => r.map(esc).join(",")).join("\n"), "fund-history.csv", "text/csv;charset=utf-8;");
  };
  const exportXLS = () => {
    const cell = (v) => `<td>${String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;")}</td>`;
    const body = [HEAD, ...exportRows()].map((r, i) => `<tr>${r.map((c) => (i === 0 ? `<th>${c}</th>` : cell(c))).join("")}</tr>`).join("");
    download(`<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1">${body}</table></body></html>`, "fund-history.xls", "application/vnd.ms-excel");
  };
  return (
    <div className="space-y-6">
      <PageTitle title="Send Fund Personally" subtitle="Credit or debit a member's fund balance manually" />
      <GlassCard className="max-w-xl p-6 sm:p-8">
        <form onSubmit={submit} className="space-y-4" data-testid="send-fund-form">
          <div>
            <label className="mb-1 block text-xs text-[var(--text-dim)]">User ID</label>
            <input className={inp} placeholder="User ID (e.g. AF100025)" value={f.member_id} onChange={upd("member_id")} data-testid="send-fund-userid" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--text-dim)]">Amount</label>
            <input type="number" min="0" step="any" className={inp} placeholder="Amount" value={f.amount} onChange={upd("amount")} data-testid="send-fund-amount" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--text-dim)]">Type</label>
            <select className={inp} value={f.type} onChange={upd("type")} data-testid="send-fund-type">
              <option value="credit">Credit Fund</option>
              <option value="debit">Debit Fund</option>
            </select>
          </div>
          <p className="rounded-lg glass-2 p-2 text-[11px] text-[var(--text-dim)]">
            Credited fund lands in the member's fund balance and can be used to activate their own or another member's ID.
          </p>
          <LuxuryButton type="submit" variant="gold" disabled={busy} data-testid="send-fund-submit">
            <Banknote size={15} className="mr-1 inline" /> {busy ? "Processing…" : "Send"}
          </LuxuryButton>
        </form>
      </GlassCard>

      <GlassCard className="p-4 sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2"><History size={18} className="text-[var(--gold)]" /><h3 className="font-semibold text-white">Fund History</h3></div>
          {records.length > 0 && (
            <div className="flex gap-2">
              <LuxuryButton size="sm" variant="dark" onClick={exportXLS} data-testid="fund-export-xls"><FileSpreadsheet size={14} className="mr-1 inline" /> Export to XLS</LuxuryButton>
              <LuxuryButton size="sm" onClick={exportCSV} data-testid="fund-export-csv"><FileText size={14} className="mr-1 inline" /> Export to CSV</LuxuryButton>
            </div>
          )}
        </div>
        <div className="mb-4 flex flex-wrap items-end gap-2">
          <div><label className="mb-1 block text-[11px] text-[var(--text-dim)]">From</label><input type="date" className={inp + " w-[160px]"} value={hf.from} onChange={(e) => setHf((s) => ({ ...s, from: e.target.value }))} data-testid="fund-from-date" /></div>
          <div><label className="mb-1 block text-[11px] text-[var(--text-dim)]">To</label><input type="date" className={inp + " w-[160px]"} value={hf.to} onChange={(e) => setHf((s) => ({ ...s, to: e.target.value }))} data-testid="fund-to-date" /></div>
          <div><label className="mb-1 block text-[11px] text-[var(--text-dim)]">Type</label><select className={inp + " w-[150px]"} value={hf.dir} onChange={(e) => setHf((s) => ({ ...s, dir: e.target.value }))} data-testid="fund-dir-filter"><option value="all">All</option><option value="credit">Credit Fund</option><option value="debit">Debit Fund</option></select></div>
          {(hf.from || hf.to || hf.dir !== "all") && <LuxuryButton size="sm" variant="dark" onClick={() => setHf({ from: "", to: "", dir: "all" })}>Clear</LuxuryButton>}
        </div>
        {loading ? <div className="skeleton h-32" /> : records.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="fund-history-table">
              <thead className="text-left text-[var(--text-dim)]">
                <tr className="border-b border-white/10">
                  <th className="py-3 pr-2">#</th><th className="py-3 pr-2">User ID</th><th className="py-3 pr-2">Name</th>
                  <th className="py-3 pr-2">Amount</th><th className="py-3 pr-2">Type</th><th className="py-3 pr-2">By Admin</th><th className="py-3 pr-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r, i) => (
                  <tr key={r.tx_id} className="row-hover border-b border-white/5" data-testid={`fund-row-${r.tx_id}`}>
                    <td className="py-3 pr-2 text-[var(--text-dim)]">{i + 1}</td>
                    <td className="py-3 pr-2 text-white">{r.member_id || "—"}</td>
                    <td className="py-3 pr-2">{r.name || "—"}</td>
                    <td className={"py-3 pr-2 " + (r.direction === "credit" ? "text-emerald-300" : "text-[var(--maroon)]")}>{r.direction === "credit" ? "+" : "-"}${money(r.amount)}</td>
                    <td className="py-3 pr-2"><StatusBadge status={r.direction === "credit" ? "approved" : "rejected"} /> <span className="ml-1 text-[var(--text-dim)]">{r.type}</span></td>
                    <td className="py-3 pr-2 text-[var(--text-dim)]">{r.admin || "—"}</td>
                    <td className="py-3 pr-2 whitespace-nowrap text-[var(--text-dim)]">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <EmptyState icon={History} title="No fund transfers yet" />}
      </GlassCard>
    </div>
  );
}
